import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { TIER_THEMES } from "@/lib/themes";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, BadgeCheck, MapPin, Instagram, Film, Send, UserCircle2, Languages } from "lucide-react";
import { toast } from "sonner";

export default function PublicProfile() {
  const { id } = useParams();
  const { user: me } = useAuth();
  const [p, setP] = useState(null);
  const [inviteMsg, setInviteMsg] = useState("Hi! I came across your CALLSHEET profile — would love to chat about a project I'm setting up.");
  const [inviteRole, setInviteRole] = useState("");

  useEffect(() => {
    api.get(`/profile/${id}`).then(({ data }) => setP(data));
  }, [id]);

  if (!p) return <div className="min-h-[60vh] flex items-center justify-center text-white/40 uppercase tracking-[0.3em] text-sm">Loading profile…</div>;

  const tier = TIER_THEMES[p.tier];
  const isMe = me?.id === p.user_id;
  const credits = [...(p.past_credits || []), ...(p.auto_credits || [])];

  const sendInvite = async () => {
    try {
      await api.post("/connections/invite", {
        to_user_id: p.user_id,
        message: inviteMsg,
        role_being_offered: inviteRole || null,
      });
      toast.success(`Invite sent to ${p.full_name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10" data-testid="public-profile">
      <Link to="/discover" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-white">
        <ArrowLeft size={14} /> Back to discover
      </Link>

      {/* Hero */}
      <div className="mt-6 cs-card rounded-3xl p-8 relative overflow-hidden cs-grain">
        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: tier?.color }} />
        <div className="grid sm:grid-cols-[auto_1fr_auto] gap-6 items-center">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-white/5 border border-white/15 flex items-center justify-center overflow-hidden">
            {p.photo_url
              ? <img src={p.photo_url} alt={p.full_name} className="w-full h-full object-cover" />
              : <UserCircle2 size={48} className="text-white/40" />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight">{p.full_name}</h1>
              {p.verified && <BadgeCheck size={22} style={{ color: tier?.color }} />}
            </div>
            <div className="mt-2 flex items-center gap-2 text-sm text-white/65">
              <span className="font-medium">{p.role_label}</span>
              <span className="text-white/30">·</span>
              <span style={{ color: tier?.color }}>{p.association_name}</span>
              {p.is_member ? (
                <span className="ml-2 text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400"
                      data-testid="pp-member-pill">
                  Member · #{p.membership_number}
                </span>
              ) : (
                <span className="ml-2 text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full bg-white/5 text-white/55 border border-white/10"
                      data-testid="pp-nonmember-pill">
                  Non-Member
                </span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-white/50">
              {p.home_city && <span className="flex items-center gap-1"><MapPin size={12} />{p.home_city}{p.home_state ? `, ${p.home_state}` : ""}</span>}
              {p.industry && <span className="uppercase tracking-[0.22em]">{p.industry}</span>}
              {p.languages?.length > 0 && (
                <span className="flex items-center gap-1"><Languages size={12} />{p.languages.join(" · ")}</span>
              )}
              {p.instagram && (
                <a href={`https://instagram.com/${p.instagram.replace("@","")}`} target="_blank" rel="noreferrer"
                   className="flex items-center gap-1 hover:text-white">
                  <Instagram size={12} />{p.instagram}
                </a>
              )}
            </div>
          </div>
          <div>
            {!isMe ? (
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="btn-themed h-11 px-6 rounded-full uppercase tracking-[0.25em] text-xs"
                          data-testid="invite-btn">
                    <Send size={14} className="mr-2" /> Invite to project
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-display uppercase tracking-tight">Send invite to {p.full_name}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 mt-3">
                    <div>
                      <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">Role you're hiring for (optional)</Label>
                      <Input value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                        className="bg-black/40 border-white/15 mt-1.5" data-testid="invite-role"
                        placeholder="e.g. Line Producer, Co-DOP, Choreographer" />
                    </div>
                    <div>
                      <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">Your message</Label>
                      <Textarea value={inviteMsg} onChange={(e) => setInviteMsg(e.target.value)}
                        className="bg-black/40 border-white/15 mt-1.5 min-h-[120px]" data-testid="invite-msg" />
                    </div>
                    <Button onClick={sendInvite} className="btn-themed w-full h-11 rounded-full uppercase tracking-[0.25em] text-xs"
                            data-testid="invite-send">
                      <Send size={14} className="mr-2" /> Send invite
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              <Link to="/profile/me/edit" className="text-xs uppercase tracking-[0.25em] text-[#F5B82E] hover:text-white">
                Edit my profile →
              </Link>
            )}
          </div>
        </div>

        {p.bio && (
          <div className="mt-6 pt-5 border-t border-white/5 text-sm text-white/75 leading-relaxed max-w-3xl">{p.bio}</div>
        )}
      </div>

      {/* Skills */}
      {p.skills?.length > 0 && (
        <div className="mt-6 cs-card rounded-2xl p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">Skills</div>
          <div className="flex flex-wrap gap-2">
            {p.skills.map((s) => (
              <span key={s} className="px-3 py-1 rounded-full text-xs border border-white/10 bg-white/5">{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Credits */}
      <div className="mt-6 cs-card rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Credits</div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/45">
            {credits.length} total · {(p.auto_credits || []).length} system-verified
          </div>
        </div>
        {credits.length === 0 ? (
          <div className="text-sm text-white/40">No credits added yet.</div>
        ) : (
          <div className="space-y-3" data-testid="credits-list">
            {credits.map((c, i) => (
              <div key={`${c.title || "credit"}-${c.year || ""}-${i}`} className="flex items-start gap-3 border-b border-white/5 last:border-0 pb-3 last:pb-0">
                <Film size={16} className="mt-0.5" style={{ color: "var(--association-theme)" }} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{c.title}</div>
                  <div className="text-xs text-white/55">
                    {c.role}{c.year ? ` · ${c.year}` : ""}{c.project_type ? ` · ${c.project_type}` : ""}
                  </div>
                  {typeof c.call_sheets_attended === "number" && (
                    <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em]"
                         style={{ color: c.status === "wrapped" ? "#34d399" : "var(--association-theme)" }}>
                      <span className="font-mono tabular-nums">{c.call_sheets_attended}</span>
                      <span>call sheet{c.call_sheets_attended === 1 ? "" : "s"} attended</span>
                      {c.status && <span className="text-white/40">· {c.status.replace("_"," ")}</span>}
                    </div>
                  )}
                  {c.notes && <div className="text-xs text-white/45 mt-1">{c.notes}</div>}
                </div>
                {c.verified && <BadgeCheck size={14} style={{ color: tier?.color }} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
